import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HolderSelect } from "../components/holders/HolderSelect";
import { PageHeader } from "../components/shell/PageHeader";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { SelectField } from "../components/ui/Input";
import { Pill } from "../components/ui/Status";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { getPage } from "../lib/api";
import { count, kwh, since } from "../lib/format";
import type { AllowanceEvent, AllowanceEventType } from "../lib/types";

/**
 * Events — spec 6.3's threshold crossings, as a feed.
 *
 * ## What is not here
 *
 * Spec 8 calls this endpoint "Allowance and operational events", and only the
 * allowance half exists: `allowance_event` is the schema's one event table, and a
 * station's status history is not stored anywhere. The page says so in its own
 * empty state rather than implying the fleet has been quiet.
 *
 * ## Why the threshold and the reading are two columns
 *
 * They answer different questions and are routinely different numbers. An
 * `overage_extended` at 110% may have fired on a session close that reached 137% —
 * spec 6.3's worked example is exactly this, an identity at 95% that starts a
 * 200 kWh session. Collapsing them into one figure would either understate the
 * overage or misreport why the event fired.
 */

const COLUMNS: readonly Column[] = [
  { key: "when", label: "When", width: "10%" },
  { key: "event", label: "Event", width: "14%" },
  { key: "pool", label: "Pool", width: "16%" },
  { key: "identity", label: "Card", width: "18%" },
  { key: "threshold", label: "Threshold", numeric: true, width: "10%" },
  { key: "reading", label: "At close", numeric: true, width: "10%" },
  { key: "consumed", label: "Consumed", numeric: true },
  { key: "quota", label: "Quota", numeric: true, width: "12%" },
];

const TYPES: readonly { readonly value: AllowanceEventType; readonly label: string }[] = [
  { value: "threshold_crossed", label: "Threshold crossed" },
  { value: "quota_exceeded", label: "Quota exceeded" },
  { value: "overage_extended", label: "Overage extended" },
];

export function Events() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const type = params.get("type") ?? "";
  const severity = params.get("severity") ?? "";
  const holderId = params.get("holderId") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;

  const events = useQuery({
    queryKey: ["events", { type, severity, holderId, page }],
    queryFn: () =>
      getPage<AllowanceEvent>("/events", { query: { type, severity, holderId, page } }),
    placeholderData: (previous) => previous,
  });

  /**
   * The warning count is its own query rather than a filter on the list: it
   * belongs in the masthead whatever the table is filtered to.
   */
  const warnings = useQuery({
    queryKey: ["events", "warning-count"],
    queryFn: () => getPage<AllowanceEvent>("/events", { query: { severity: "warning", limit: 1 } }),
  });

  const rows = events.data?.rows ?? [];

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Events"
        metrics={
          <>
            <Metric label="Total" value={count(events.data?.meta?.total)} hint="matching filters" />
            <Metric
              label="At or over quota"
              value={count(warnings.data?.meta?.total)}
              hint="all time"
              tone={(warnings.data?.meta?.total ?? 0) > 0 ? "signal" : "muted"}
            />
          </>
        }
        lede="Threshold crossings, evaluated once per session at close. Nothing here refused or interrupted a charge — recording the event is the whole enforcement action."
      />

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <SelectField
          label="Event"
          value={type}
          onChange={(event) => setFilter("type", event.target.value)}
        >
          <option value="">Any</option>
          {TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Severity"
          value={severity}
          onChange={(event) => setFilter("severity", event.target.value)}
        >
          {/* Two values, because spec 6.3 assigns exactly two. Offering anything
              else would be a filter that can never match. */}
          <option value="">Any</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
        </SelectField>

        <HolderSelect
          label="Pool"
          value={holderId}
          emptyLabel="Any"
          onChange={(value) => setFilter("holderId", value)}
          hint="The holder the policy is set on. Events are recorded against the pool, not the card."
        />
      </form>

      {events.isError ? (
        <ErrorNotice error={events.error} onRetry={() => void events.refetch()} />
      ) : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={COLUMNS} />
          {events.isPending ? (
            <SkeletonRows rows={8} columns={COLUMNS.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  index={index}
                  onActivate={() => navigate(`/holders/${row.holderId}`)}
                >
                  <Cell muted>{since(row.createdAt)}</Cell>
                  <Cell>
                    <Pill tone={row.severity === "warning" ? "faulted" : "idle"}>
                      {label(row.type)}
                    </Pill>
                  </Cell>
                  <Cell>
                    <span className="font-medium text-ink">{row.holderName}</span>
                  </Cell>
                  {/* The card whose session closed the gap — one of possibly many
                      in the pool. Gone once that session has been swept (spec 12). */}
                  <Cell muted={row.identityLabel === null}>
                    {row.identityLabel === null ? (
                      "—"
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        <span>{row.identityLabel}</span>
                        <span className="text-micro tracking-normal normal-case text-ink-faint">
                          {row.idTag}
                        </span>
                      </div>
                    )}
                  </Cell>
                  <Cell numeric>{row.thresholdPct}%</Cell>
                  {/*
                    The figure at close, beside the threshold that fired. Emphasised
                    when the two disagree, because that gap is the thing spec 6.3
                    warns about: a session can jump several ladder steps at once.
                  */}
                  <Cell
                    numeric
                    muted={row.consumedPct === null || row.consumedPct === row.thresholdPct}
                  >
                    {row.consumedPct === null ? "—" : `${row.consumedPct}%`}
                  </Cell>
                  <Cell numeric>{kwh(row.consumedWh)} kWh</Cell>
                  <Cell numeric muted>
                    {kwh(row.quotaWh)} kWh
                  </Cell>
                </TableRow>
              ))}
            </tbody>
          )}
        </TableFrame>

        {!events.isPending && rows.length === 0 ? (
          <EmptyState
            title={
              type === "" && severity === ""
                ? "No allowance thresholds have been crossed."
                : "No events match these filters."
            }
            detail="Thresholds are evaluated when a session closes, against the period's committed energy. A card with no allowance assigned is tracked and never flagged, so it produces no events. Station faults and configuration drift are not recorded as events in this build — they are on each charge point's own page."
          />
        ) : null}

        <Pagination
          meta={events.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>
    </>
  );
}

function label(type: AllowanceEventType): string {
  return TYPES.find((option) => option.value === type)?.label ?? type;
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
